import { useCallback } from 'react';

import { useUtilityContext } from '@change-corgi/core/react/utilityContext';

import { useCookiePrefAsync } from 'src/app/shared/hooks/session';
import { isLoaded } from 'src/app/shared/utils/async';

import { getCookieName } from './shared/getCookieName';
import type { BannerType, TosConfig } from './types';

type Result = {
	getCount: () => number;
	decreaseCount: (count?: number) => void;
};

function getMaxDisplayCount(config: TosConfig) {
	return config?.maxDisplayCount || 0;
}
function getCookieExpiration(bannerType: BannerType | undefined, config: TosConfig) {
	return config?.[bannerType === 'post' ? 'postEffectiveRange' : 'preEffectiveRange'];
}

export function useCookie(bannerType: BannerType | undefined, config: TosConfig): Result {
	const cookieAllowedState = useCookiePrefAsync('preferences');
	const cookieAllowed = isLoaded(cookieAllowedState) ? cookieAllowedState.value : false;

	const cookieName = getCookieName(bannerType, config?.effectiveDate);
	const { cookies } = useUtilityContext();

	const getDisplayCount = useCallback(() => {
		const maxDisplayCount = getMaxDisplayCount(config);
		if (!maxDisplayCount || !cookieName) return 0;
		const displayCountStr = cookies.get(cookieName);
		if (!displayCountStr || !/^\d+$/.test(displayCountStr)) return maxDisplayCount;
		try {
			return parseInt(displayCountStr, 10);
		} catch (e) {
			return 0;
		}
	}, [cookies, cookieName, config]);

	return {
		getCount: getDisplayCount,
		decreaseCount: useCallback(
			(count?: number) => {
				const expires = getCookieExpiration(bannerType, config);
				if (!cookieName || !cookieAllowed || !expires) return;
				cookies.set(cookieName, `${Math.max(0, count === undefined ? getDisplayCount() - 1 : count)}`, {
					path: '/',
					expires,
				});
			},
			[cookies, config, cookieName, cookieAllowed, bannerType, getDisplayCount],
		),
	};
}
